import { useState, useCallback } from 'react';
import styled from '@emotion/styled';
import { Button, PageHeader, Text, tokens } from '@hotjar/hotsauce';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { FEEDBACK } from '@hotjar/webapp/constants/routes';
import { Page } from '@hotjar/webapp/components/Page';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { EntityActionsDropdown } from '@hotjar/feedback/components/common/EntityActions/EntityActionsDropdown';
import { EntityDeleteModal } from '@hotjar/feedback/components/common/EntityActions/EntityDeleteModal';
import { EntityRenameModal } from '@hotjar/feedback/components/common/EntityActions/EntityRenameModal';
import { useCurrentSite } from '@hotjar/site';
import { SharingModule } from '@hotjar/webapp/components/SharingModule';
import {
  useDeleteFeedbackWidgetMutation,
  useRenameFeedbackWidgetMutation,
  useFeedbackWidgetQuery,
} from '@hotjar/feedback';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { useTranslation } from 'react-i18next';
import { EntityCreator } from '../../../common/EntityCreator';
import { FeedbackActivationSwitch } from './FeedbackActivationSwitch';

const StyledPage = styled(Page)`
  margin-bottom: ${tokens.spacer.x2};
`;

export interface FeedbackResponsesHeaderProps {
  widgetId: number;
}

export function FeedbackResponsesHeader({
  widgetId,
}: FeedbackResponsesHeaderProps): JSX.Element | null {
  const { t } = useTranslation('feedback');
  const { data: currentSite } = useCurrentSite();
  const widget = useFeedbackWidgetQuery(currentSite, widgetId).data;

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeletePending, setDeletePending] = useState(false);

  const { success, error } = useNotify();

  const deleteFeedbackWidgetMutation = useDeleteFeedbackWidgetMutation();

  const navigateToWidgetEdit = useCallback(() => {
    if (!widget || !currentSite) return;
    navigateTo(FEEDBACK.EDIT.path, { siteId: currentSite.id, feedbackWidgetId: widget.id });
  }, [widget, currentSite]);

  const navigateToWidgetDuplicate = useCallback(() => {
    if (!widget || !currentSite) return;
    navigateTo(FEEDBACK.CREATE.path, { siteId: currentSite.id, copyId: widget.id });
  }, [currentSite, widget]);

  const navigateToWidgetCreate = useCallback(() => {
    if (!currentSite) return;
    navigateTo(FEEDBACK.CREATE.path, { siteId: currentSite.id });
  }, [currentSite]);

  const deleteWidget = useCallback(() => {
    if (!widget || !currentSite) return;
    setDeletePending(true);
    deleteFeedbackWidgetMutation
      .mutateAsync({ site: currentSite, feedbackWidgetId: widget.id })
      .then(
        () => {
          success(t('feedback-actions.delete-success'));
          if (currentSite.id) {
            navigateTo(FEEDBACK.RESPONSES_DEFAULT.path, { siteId: currentSite.id });
          }
        },
        () => {
          error(t('feedback-actions.delete-error'));
          setDeletePending(false);
        },
      );
  }, [widget, currentSite, deleteFeedbackWidgetMutation, success, t, error]);

  const hasFeedbackDeletePermission = usePermission('feedbacks-delete');
  const hasFeedbackCreatePermission = usePermission('feedbacks-create');
  const hasFeedbackUpdatePermission = usePermission('feedbacks-update');

  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [isRenamePending, setRenamePending] = useState(false);
  const renameFeedbackWidgetMutation = useRenameFeedbackWidgetMutation();
  const onRename = useCallback(
    async (name: string) => {
      if (!widget || !currentSite) return;

      setRenamePending(true);
      try {
        await renameFeedbackWidgetMutation.mutateAsync({
          site: currentSite,
          feedbackWidgetId: widget.id,
          name,
        });
      } catch {
        error(t('feedback-actions.widget-error'));
        return;
      } finally {
        setRenamePending(false);
      }
      setRenameModalOpen(false);
      success(t('feedback-actions.widget-renamed'));
    },
    [widget, currentSite, success, t, renameFeedbackWidgetMutation, error],
  );
  return (
    <StyledPage>
      <PageHeader
        title={widget?.name ?? t('feedback-responses.header.default-title')}
        subtitle={
          widget !== undefined && widget.description !== null && widget.description.length > 0 ? (
            <Text textType="body2">{widget.description}</Text>
          ) : undefined
        }
        actions={
          <>
            {widget !== undefined && currentSite !== undefined && (
              <>
                <EntityCreator
                  createdBy={widget.created_by_user}
                  createdTime={widget.created_epoch_time}
                />
                <>
                  <FeedbackActivationSwitch
                    hasPermission={hasFeedbackUpdatePermission}
                    feedbackWidgetId={widgetId}
                    site={currentSite}
                    isEnabledInitalState={widget.enabled}
                  />

                  <EntityActionsDropdown
                    placement="bottom-start"
                    onDelete={
                      hasFeedbackDeletePermission ? () => setDeleteModalOpen(true) : undefined
                    }
                    onEdit={hasFeedbackUpdatePermission ? navigateToWidgetEdit : undefined}
                    onDuplicate={
                      hasFeedbackCreatePermission ? navigateToWidgetDuplicate : undefined
                    }
                    onRename={
                      hasFeedbackUpdatePermission ? () => setRenameModalOpen(true) : undefined
                    }
                  />
                </>
              </>
            )}
            <SharingModule
              trigger={
                <Button size="s" kind="secondary">
                  {t('common:actions.share')}
                </Button>
              }
            />
            <Button
              kind="primary"
              size="s"
              icon="plus"
              isDisabled={!hasFeedbackCreatePermission}
              onClick={navigateToWidgetCreate}
            >
              {t('feedback-responses.header.new-widget')}
            </Button>
          </>
        }
      />
      {widget && isDeleteModalOpen && (
        <EntityDeleteModal
          entityName={widget.name}
          entityType="widget"
          isDeletePending={isDeletePending}
          isOpen={isDeleteModalOpen}
          onDelete={deleteWidget}
          onClose={() => setDeleteModalOpen(false)}
        />
      )}
      {widget && isRenameModalOpen && (
        <EntityRenameModal
          entityName={widget.name}
          entityType="widget"
          isRenamePending={isRenamePending}
          isOpen={isRenameModalOpen}
          onRename={onRename}
          onClose={() => setRenameModalOpen(false)}
        />
      )}
    </StyledPage>
  );
}
